<script setup>
import { useI18n } from 'vue-i18n'
import { Link } from '@inertiajs/vue3'
import { useCurrency } from '@/composables/useCurrency'
import { useInboxStore } from '@/stores/useInboxStore'
import RentalStatusPill from '@/Components/UI/RentalStatusPill.vue'
import PaymentStatusPill from '@/Components/UI/PaymentStatusPill.vue'
import DepositStatusPill from '@/Components/UI/DepositStatusPill.vue'
import Icon from '@/Components/UI/Icon.vue'
import { ref } from 'vue'

const props = defineProps({
  rental:         { type: Object, required: true },
  conversationId: { type: Number, required: true }
})

const { t } = useI18n()
const { formatDate, formatMoney } = useCurrency()
const store = useInboxStore()

const sending = ref(false)
const sendError = ref(null)

const onSendSigningLink = async () => {
  if (sending.value) return
  sending.value = true
  sendError.value = null
  try {
    const body = new FormData()
    body.append('via', 'inbox')
    body.append('conversation_id', String(props.conversationId))
    const res = await fetch(`/rentals/${props.rental.id}/signing_link`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
      },
      body,
    })
    if (!res.ok && res.status !== 302) {
      sendError.value = 'send_failed'
    }
  } catch (e) {
    sendError.value = e.message || 'network'
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <section class="rounded-[var(--radius-lg)] border border-[color:var(--line)] p-3">
    <div class="flex items-center justify-between gap-2 mb-2 flex-wrap">
      <span class="text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
        {{ t('inbox.drawer.activeRental') }}
      </span>
      <div class="flex items-center gap-1.5 flex-wrap">
        <RentalStatusPill :status="rental.status" />
        <PaymentStatusPill v-if="rental.payment_status" :status="rental.payment_status" />
        <DepositStatusPill v-if="rental.deposit_status" :status="rental.deposit_status" />
      </div>
    </div>
    <Link :href="`/rentals/${rental.id}`"
          class="block fw-value text-sm text-[color:var(--ink)] hover:text-[color:var(--accent)]">
      {{ rental.vehicle_label }}
    </Link>
    <div class="text-xs text-[color:var(--muted)]">
      {{ formatDate(rental.start_at) }} → {{ formatDate(rental.expected_end_at) }}
    </div>
    <div v-if="rental.balance_due_cents > 0" class="text-xs text-[color:var(--warn)] fw-value mt-1">
      {{ t('inbox.drawer.balanceDue') }}: {{ formatMoney(rental.balance_due_cents) }}
    </div>
    <div class="flex items-center gap-3 mt-3">
      <Link v-if="rental.has_gps" :href="`/rentals/${rental.id}/gps`"
            class="inline-flex items-center gap-1 text-xs text-[color:var(--accent)] underline">
        <Icon name="pin" :size="14" /> {{ t('rental.show.gps') }}
      </Link>
      <button v-if="rental.has_contract && rental.signing_status !== 'awaiting'"
              type="button"
              class="inline-flex items-center gap-1 text-xs text-[color:var(--accent)] underline disabled:opacity-50"
              :disabled="sending || (rental.contract_blockers?.length > 0)"
              @click="onSendSigningLink">
        {{ t('inbox.drawer.sendForSigning') }}
      </button>
      <span v-else-if="rental.signing_status === 'awaiting'"
            class="inline-flex items-center gap-1 text-xs text-[color:var(--muted)]">
        {{ t('inbox.drawer.awaitingSignature') }}
      </span>
    </div>
    <p v-if="sendError" class="mt-2 text-xs text-[color:var(--danger)]">
      {{ t('inbox.drawer.shareAgreementFailed') }}
    </p>
  </section>
</template>
